// ============================================================================
// src/lib/processingDateBooking.ts — is this processing date booked?
// ----------------------------------------------------------------------------
// Ronnie's note 9 and design rule Q14-A: a date the processor has booked shows
// as a plain black date with a gray "Booked with <processor>"; a forecast or
// target shows as a gray date with "est.".
//
//   broiler        every date is booked with Atlanta (Ronnie, 2026-10-08).
//   cattle, sheep  a Processing record exists only once the batch is scheduled
//                  with the processor (Schedule Processing books the date) or
//                  sent, so its date is booked.
//   pig            a sent trip, or a planned trip locked as scheduled with the
//                  processor, is booked; any other planned trip is an estimate.
//
// Milestones, records whose planner source no longer resolves, and completed
// records (the date is now a fact, not a booking) print as a plain date.
// ============================================================================

/** The one processor every broiler date is booked with. */
export const BROILER_PROCESSOR = 'Atlanta Poultry Processing';

export type DateBooking = {kind: 'booked'; processor: string | null} | {kind: 'estimate'} | {kind: 'plain'};

type BookingRecord = {
  record_type?: string | null;
  source_kind?: string | null;
  /** The phase stored on the Processing record; a fallback only. */
  source_phase?: string | null;
  processor?: string | null;
  completed?: boolean;
  source?: {matched?: boolean | null; phase?: string | null; scheduled_with_processor?: boolean | null} | null;
};

/**
 * The live planner phase wins over the phase stored on the record, which can
 * lag (a planned trip sent since the record was written still reads 'planned').
 * Every surface passes both raw values and lets this decide, so the schedule
 * and the drawer can never disagree.
 */
export function effectiveSourcePhase(rec: Pick<BookingRecord, 'source_phase' | 'source'>): string | null {
  return rec.source?.phase || rec.source_phase || null;
}

export function processingDateBooking(rec: BookingRecord): DateBooking {
  if (rec.record_type === 'milestone' || rec.completed) return {kind: 'plain'};
  if (!rec.source || rec.source.matched === false) return {kind: 'plain'};
  const processor = rec.processor?.trim() || null;
  switch (rec.source_kind) {
    case 'broiler':
      return {kind: 'booked', processor: processor ?? BROILER_PROCESSOR};
    case 'cattle':
    case 'sheep':
      return {kind: 'booked', processor};
    case 'pig':
      return effectiveSourcePhase(rec) === 'actual' || rec.source.scheduled_with_processor === true
        ? {kind: 'booked', processor}
        : {kind: 'estimate'};
    default:
      return {kind: 'plain'};
  }
}

/** "Booked with Homestead", or "Booked" while no processor is recorded. */
export function bookedWith(processor: string | null): string {
  return processor ? `Booked with ${processor}` : 'Booked';
}
